import { InfoCircleOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Button, Checkbox, Input, Select, Tooltip } from 'antd'
import type { HookAPI } from 'antd/es/modal/useModal'
import { useState } from 'react'
import { toast } from 'sonner'

import AtomSwitch from '@/components/Atoms/AtomSwitch/AtomSwitch'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import { stockImportsQueries } from '../api/stockImportsQueries'
import { useExportMissingSkuMutation } from '../hooks/useExportMissingSkuMutation'
import {
  DEFAULT_MISSING_SKU_FILTERS,
  describeMissingSkuExport,
  MISSING_SKU_REASON_OPTIONS,
  type MissingSkuFilters,
  toMissingSkuQueryParams,
} from '../lib/stockImportsMissingSku'
import {
  MISSING_SKU_ISSUE_TYPE_OPTIONS,
  PRICE_ISSUE_REASON_LABELS,
} from '../lib/stockImportsPriceLabels'
import stockCn from '../StockImportsTab/StockImportsTab.module.scss'
import StockImportsVirtualTable from '../StockImportsVirtualTable/StockImportsVirtualTable'
import type { MissingSkuItemDto } from '../types/stockImports.types'
import type { PriceIssueReason } from '../types/stockImportsPrices.types'
import {
  missingSkuColumns,
  missingSkuColumnsWithPrices,
} from './missingSkuColumns'
import MissingSkuExportConfirm from './MissingSkuExportConfirm/MissingSkuExportConfirm'
import cn from './MissingSkuReport.module.scss'

interface MissingSkuReportProps {
  logId: string
  totalMissingRows?: number
  modal: HookAPI
  canSeePrices?: boolean
}

const EMPTY_ITEMS: MissingSkuItemDto[] = []

const PRICE_REASON_OPTIONS = (
  Object.keys(PRICE_ISSUE_REASON_LABELS) as PriceIssueReason[]
).map((reason) => ({ value: reason, label: PRICE_ISSUE_REASON_LABELS[reason] }))

const getRowKey = (row: MissingSkuItemDto, index: number) =>
  `${row.fileName ?? 'file'}-${row.sheetName ?? 'sheet'}-${row.rowIndex}-${index}`

function MissingSkuReport({
  logId,
  totalMissingRows,
  modal,
  canSeePrices = false,
}: MissingSkuReportProps) {
  const [filters, setFilters] = useState<MissingSkuFilters>(
    DEFAULT_MISSING_SKU_FILTERS
  )
  const [articleDraft, setArticleDraft] = useState('')
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const params = toMissingSkuQueryParams(filters)
  const missingSkuQuery = useInfiniteQuery(
    stockImportsQueries.missingSku(logId, params)
  )
  const exportMissingSku = useExportMissingSkuMutation()

  const items =
    missingSkuQuery.data?.pages.flatMap((page) => page.content) ?? EMPTY_ITEMS
  const filteredCount = missingSkuQuery.data?.pages[0]?.totalElements ?? 0
  const hasNothingToExport = missingSkuQuery.isSuccess && filteredCount === 0

  const updateFilters = (patch: Partial<MissingSkuFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch }))
  const showsPrices = filters.issueType !== 'STOCK'

  const handleExport = () => {
    const { title, items: filterItems } = describeMissingSkuExport(
      filters,
      filteredCount,
      totalMissingRows
    )
    modal.confirm({
      title: 'Выгрузка в Excel',
      content: <MissingSkuExportConfirm title={title} items={filterItems} />,
      okText: 'Скачать',
      cancelText: 'Отмена',
      centered: true,
      onOk: () =>
        exportMissingSku.mutate(
          { logId, params },
          {
            onSuccess: () => toast.success('Файл скачан'),
            onError: (error) => handleApiError(error, modal),
          }
        ),
    })
  }

  return (
    <div className={cn.report}>
      {canSeePrices && (
        <AtomSwitch
          options={MISSING_SKU_ISSUE_TYPE_OPTIONS}
          value={filters.issueType}
          onChange={(issueType) => updateFilters({ issueType })}
          ariaLabel="Какие проблемы строк показывать"
          isOrganisationSelected={isOrganisationSelected}
        />
      )}
      <div className={cn.toolbar}>
        <Input.Search
          allowClear
          value={articleDraft}
          onChange={(event) => setArticleDraft(event.target.value)}
          onSearch={(value) =>
            updateFilters({ article: value.trim() || undefined })
          }
          placeholder="Поиск по артикулу"
          className={cn.search}
        />
        <Select
          mode="multiple"
          allowClear
          value={filters.reasons}
          options={MISSING_SKU_REASON_OPTIONS}
          onChange={(reasons) => updateFilters({ reasons })}
          placeholder="Причина"
          maxTagCount="responsive"
          popupMatchSelectWidth={false}
          className={cn.reasons}
        />
        {showsPrices && (
          <Select
            mode="multiple"
            allowClear
            value={filters.priceReasons}
            options={PRICE_REASON_OPTIONS}
            onChange={(priceReasons) => updateFilters({ priceReasons })}
            placeholder="Проблема цены"
            maxTagCount="responsive"
            popupMatchSelectWidth={false}
            className={cn.reasons}
          />
        )}
        <div>
          <Checkbox
            checked={filters.hideZeroStock}
            onChange={(event) =>
              updateFilters({ hideZeroStock: event.target.checked })
            }
          >
            Скрыть нулевые остатки по найденным товарам
          </Checkbox>
          <Tooltip
            title={
              <>
                <p>
                  Скрывает строки, где у поставщика нулевой остаток, а в
                  каталоге нашлось несколько подходящих товаров. Эти товары у
                  нас уже есть, создавать их не нужно.
                </p>
                <p>
                  Строки с товарами, которых нет в каталоге, остаются в отчёте с
                  любым остатком. Удобно, чтобы выгрузить в Excel только то, по
                  чему нужно создать номенклатуру.
                </p>
              </>
            }
          >
            <InfoCircleOutlined
              className={cn.icon}
              aria-label="Что скрывает этот флажок"
            />
          </Tooltip>
        </div>
        <span className={cn.count}>
          Показано {filteredCount}
          {totalMissingRows === undefined ? '' : ` из ${totalMissingRows}`}
        </span>
        <Tooltip title={hasNothingToExport ? 'Нет строк для выгрузки' : ''}>
          <Button
            onClick={handleExport}
            loading={exportMissingSku.isPending}
            disabled={!missingSkuQuery.isSuccess || hasNothingToExport}
          >
            Скачать XLSX
          </Button>
        </Tooltip>
      </div>
      {missingSkuQuery.isError ? (
        <div className={cn.error}>
          Не удалось загрузить отчёт.{' '}
          <Button type="link" onClick={() => missingSkuQuery.refetch()}>
            Повторить
          </Button>
        </div>
      ) : (
        <StockImportsVirtualTable
          data={items}
          columns={
            showsPrices ? missingSkuColumnsWithPrices : missingSkuColumns
          }
          rowKey={getRowKey}
          emptyText="Нет незагруженных позиций"
          loading={
            missingSkuQuery.isPending || missingSkuQuery.isPlaceholderData
          }
          loadingMore={missingSkuQuery.isFetchingNextPage}
          hasMore={missingSkuQuery.hasNextPage}
          onLoadMore={() => missingSkuQuery.fetchNextPage()}
          rowClassName={(row) =>
            row.zeroStock ? stockCn.previewWarningRow : undefined
          }
          minTableWidth="100%"
          estimateRowHeight={84}
          height="min(480px, calc(100dvh - 320px))"
          compact
        />
      )}
    </div>
  )
}

export default MissingSkuReport
